import { isEmpty } from 'lodash'

import axiosInstance from 'api/axiosInstance'
import { type PostForUpload } from 'types/api/post'
import { type SortDirection } from 'types/enums'
import { type IGetViewLikesUsersListResponse } from 'types/interfaces/post'
import { type IGetViewPollParticipantsResponse } from 'types/interfaces/post/IGetViewPollParticipantsResponse'

import { type ITipPayload } from '../../types/interfaces/ITypes'

type PublicId = string

export const uploadPost = async (post: PostForUpload) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: '/api/post',
        data: post
    })

    return data
}

export const uploadReel = async (post: PostForUpload) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: '/api/reel',
        data: post
    })

    return data
}

export const deletePost = async (postId: PublicId) => {
    const { data } = await axiosInstance({
        method: 'DELETE',
        url: `/api/post/${postId}`
    })

    return data
}

export const uploadPoll = async (poll: any) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: '/api/post/poll',
        data: poll
    })

    return data
}

export const editPoll = async (
    poll: any,
    id: PublicId
) => {
    const { data } = await axiosInstance({
        method: 'PUT',
        url: `/api/post/poll/${id}`,
        data: poll
    })

    return data
}

export const uploadGoal = async (goal: any) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: '/api/post/goal',
        data: goal
    })

    return data
}

export const sendTipToPost = async (
    payload: ITipPayload
) => {
    const { post_id, ...data } = payload

    const { data: response } = await axiosInstance({
        method: 'POST',
        url: `/api/user/post/${post_id}/tip`,
        data
    })

    return response
}

export const pinPost = async (
    postId: PublicId
) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: `/api/post/pin/${postId}`
    })

    return data
}

export type PostLiveStatsInclude =
    | 'metrics'
    | 'monetization'

export interface IPostLiveStats {
    post_id: PublicId

    views?: number
    unique_views?: number
    views_raw?: number
    views_unique?: number
    impressions_raw?: number
    unique_impressions?: number
    impressions_unique?: number

    overall_revenue?: number
    purchases_count?: number
    purchases_sum?: number
    tips_count?: number
    tips_sum?: number

    conversion_rate?: number
    media_group_conversion_rate?: number | null
}

interface IPostLiveStatsResponse {
    status: string
    code: number
    data: IPostLiveStats
}

export const getPostLiveStats = async (
    postId: PublicId,
    include?: PostLiveStatsInclude[]
): Promise<IPostLiveStats> => {
    const { data } =
        await axiosInstance<IPostLiveStatsResponse>({
            method: 'GET',
            url: `/api/post/${postId}/live-stats`,
            params: include?.length
                ? {
                    include: include.join(',')
                }
                : undefined
        })

    return data.data
}

export const answerPoll = async (variables: {
    pollId: PublicId
    answer?: string | number | null
    answerId?: PublicId
}) => {
    const {
        pollId,
        answer,
        answerId
    } = variables

    let answerData:
        | {
            answer: string | number
        }
        | {
            answer_id: PublicId
        }
        | undefined

    if (
        answer !== undefined &&
        answer !== null &&
        answer !== ''
    ) {
        answerData = {
            answer
        }
    }

    if (answerId) {
        answerData = {
            answer_id: answerId
        }
    }

    const { data } = await axiosInstance({
        method: 'POST',
        url: `/api/post/poll/${pollId}/answer`,
        data: answerData
    })

    return data
}

export const editPost = async (
    post: any,
    id: PublicId
) => {
    const { data } = await axiosInstance({
        method: 'PUT',
        url: `/api/post/${id}`,
        data: post
    })

    return data
}

export const editPostPrice = async (
    price: {
        price: number | string
    },
    id: PublicId
) => {
    const { data } = await axiosInstance({
        method: 'PUT',
        url: `/api/post/change-post-price/${id}`,
        data: price
    })

    return data
}

export const purchasePremiumPost = async (payload: {
    postId: PublicId
    paymentMethod: 'wallet' | 'card'
    cardId: string | undefined
}) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: '/api/post/buy',
        data: payload
    })

    return data
}

export const getSinglePost = async (
    postId: PublicId
) => {
    console.trace('getSinglePost called with:', postId, typeof postId)

    const { data } = await axiosInstance({
        method: 'GET',
        url: `/api/post/${postId}`
    })

    return data
}

export const getSinglePostForEdit = async (
    postId: PublicId
) => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: `/api/post/${postId}/edit`
    })

    return data
}

export const getPostCommentsInfinite = async (
    id: PublicId,
    currentPage: number = 1
) => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: `/api/post/${id}/comments`,
        params: {
            page: currentPage
        }
    })

    return {
        nextCursor:
            currentPage < data.meta.last_page
                ? currentPage + 1
                : undefined,

        prevCursor:
            currentPage > 1
                ? currentPage - 1
                : undefined,

        page: {
            data
        }
    }
}

export const postNewComment = async (
    id: PublicId,
    comment: string
) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: `/api/comment/post/${id}`,
        data: {
            comment
        }
    })

    return data
}

export const postNewReply = async (tmpData: {
    id: PublicId
    reply: string
}) => {
    const {
        id,
        reply
    } = tmpData

    const { data } = await axiosInstance({
        method: 'POST',
        url: `/api/comment/comment/${id}`,
        data: {
            comment: reply
        }
    })

    return data
}

export const getCommentReplies = async (
    id: PublicId
) => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: `/api/comment/${id}/comments`
    })

    return data
}

export const getComment = async (
    id: PublicId
) => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: `/api/comment/${id}`
    })

    return data
}

export const postStorySeen = async (
    id: PublicId
) => {
    const { data } = await axiosInstance({
        method: 'POST',
        url: `/api/story/view/${id}`
    })

    return data
}

export const getViewLikesUsersList = async (
    postId: PublicId,
    sortDirection: SortDirection,
    page: number,
    searchTerm: string
) => {
    const params: {
        sort_value: SortDirection
        page: number
        search?: string
    } = {
        sort_value: sortDirection,
        page
    }

    if (!isEmpty(searchTerm)) {
        params.search = searchTerm
    }

    const response =
        await axiosInstance<IGetViewLikesUsersListResponse>({
            method: 'GET',
            url: `/api/like/post/${postId}/users`,
            params
        })

    return response.data
}

export const getViewPollParticipants = async (
    pollId: PublicId,
    answerId: PublicId,
    pageParam: number
) => {
    const response =
        await axiosInstance<IGetViewPollParticipantsResponse>({
            method: 'GET',
            url: `/api/post/poll/${pollId}/answer/${answerId}/users`,
            params: {
                page: pageParam
            }
        })

    return response.data
}
